<template>
  <div class="docs-container container mt-4">
    <h1>📊 TableField</h1>
    <p>A powerful and flexible Vue table component designed for admin panels and data-heavy interfaces.</p>

    <hr />

    <h2>✨ Features</h2>
    <ul>
      <li>🔍 Real-time search</li>
      <li>🔃 Column sorting</li>
      <li>🎛 Custom filters</li>
      <li>📄 Pagination</li>
      <li>🧩 Column visibility control</li>
      <li>📱 Mobile-friendly layout</li>
      <li>🌐 Works with API or local data</li>
    </ul>

    <hr />

    <h2>🚀 Quick Example</h2>
    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField
          v-model:params="params"
          :data="users"
          :columns="columns"
          showSearch
          columnChooser
          section="users"
          storeSearch
          @fetch-data="loadUsers"
          @action="handleAction"
      /&gt;
    </code></pre>

    <hr />

    <h2>⚙️ Props & Configuration</h2>

    <h3>Data & State</h3>
    <table class="table table-striped">
      <thead>
      <tr><th>Property</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>

      <tr>
        <td>data</td><td>Array</td><td>[]</td>
        <td>Array of table rows to display. Each item represents a row.</td>
      </tr>

      <tr>
        <td>columns</td><td>Array</td><td>[]</td>
        <td>
          Column configuration array.<br /><br />

          <strong>Example:</strong>
          <pre class="p-2 bg-white text-dark"><code class="language-javascript">
            columns: [
              {
                key: "vehicleNumber",
                title: "Vehicle Number",
                sortable: true,
                minWidth: "250px",
                maxWidth: "400px",
                align: "justify",
                wrap: true
              },
              {
                key: "quantity",
                title: "Quantity",
                sortable: true,
                align: "right",
                minWidth: "100px"
              },
              {
                key: "actions",
                actions: [
                  {
                    emit: "sendMessage",
                    label: (row) => row.blokedTrackpro == '1'
                      ? this.$t('debtors.btn-blocked')
                      : this.$t('debtors.btn-active'),
                    class: (row) => row.blokedTrackpro == '1'
                      ? "btn-sm btn-warning"
                      : "btn-sm btn-success",
                    tooltip: (row) => row.blokedTrackpro == '1'
                      ? "Blocked"
                      : "Active"
                  },
                  { emit: "onEdit", icon: "bi bi-pencil-square", class: "btn-success", tooltip: "Edit" },
                  { emit: "onDelete", icon: "bi bi-trash3", class: "btn-danger", tooltip: "Delete" }
                ]
              }
            ]
          </code></pre>

          <strong>Column Options:</strong>
          <ul>
            <li><code>sortable</code> – Enables column sorting.</li>
            <li><code>minWidth</code> – Minimum column width (e.g. <code>"150px"</code>).</li>
            <li><code>maxWidth</code> – Maximum column width to prevent layout stretching.</li>
            <li><code>align</code> – Text alignment: <code>left</code>, <code>center</code>, <code>right</code>, <code>justify</code>.</li>
            <li><code>wrap</code> – Controls text wrapping:
              <ul>
                <li><code>true</code> → text wraps to next line</li>
                <li><code>false</code> → text stays in one line with ellipsis + tooltip</li>
              </ul>
            </li>
            <li><code>actions</code> – Defines buttons in the "Actions" column.</li>
          </ul>

          <strong>Dynamic Actions (Advanced):</strong>
          <ul>
            <li>You can pass functions to dynamically control button behavior per row.</li>
            <li>Each function receives the current <code>row</code> as argument.</li>
          </ul>

          <pre class="p-2 bg-white text-dark"><code class="language-javascript">
            {
              emit: "sendMessage",
              label: (row) => row.blokedTrackpro == '1' ? "Blocked" : "Active",
              class: (row) => row.blokedTrackpro == '1' ? "btn-warning" : "btn-success",
              tooltip: (row) => row.blokedTrackpro == '1' ? "Blocked" : "Active"
            }
          </code></pre>

          <strong>Supported dynamic properties:</strong>
          <ul>
            <li><code>label</code> – Button text (String | Function)</li>
            <li><code>class</code> – CSS classes (String | Function)</li>
            <li><code>tooltip</code> – Tooltip text (String | Function)</li>
            <li><code>show</code> – Function to conditionally display button</li>
            <li><code>icon</code> – Icon class (optional)</li>
          </ul>

          <strong>Notes:</strong>
          <ul>
            <li>Functions are executed for each row.</li>
            <li>If <code>show</code> is not provided → action is always visible.</li>
            <li>Make sure to handle data types correctly (e.g. <code>Number(row.value)</code>).</li>
            <li>Works in both desktop and mobile table modes.</li>
          </ul>

        </td>
      </tr>

      <tr>
        <td>params</td>
        <td>Object</td>
        <td>
          { page:1, pageSize:10, search:null, sort_column:null, sort_direction:null }
        </td>
        <td>
          Table state including pagination, search, sorting, and filters.<br /><br />

          <strong>Example:</strong>
          <pre class="p-2 bg-white text-dark"><code class="language-javascript">
            params: {
              page: 1,
              pageSize: 20,
              total: null,
              last_page: null,
              search: null,
              sort_column: null,
              sort_direction: null,
              filter: {}
            }
          </code></pre>

          <strong>Behavior:</strong>
          <ul>
            <li>If <code>v-model:params</code> is provided → pagination, sorting, and search are enabled.</li>
            <li>If not provided → pagination is hidden automatically.</li>
            <li>Works with both API mode and local mode.</li>
          </ul>
        </td>
      </tr>

      </tbody>
    </table>

    <h3>UI Options & Behavior</h3>
    <table class="table table-striped">
      <thead>
      <tr><th>Property</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>
      <tr><td>showPageSize</td><td>Boolean</td><td>true</td><td>Shows the page size selector in pagination.</td></tr>
      <tr><td>rowClass</td><td>Function</td><td>() =&gt; ""</td><td>Function to assign custom classes or styles to a row. Receives row object.</td></tr>
      <tr><td>cellClass</td><td>Function</td><td>() =&gt; ""</td><td>Function to assign custom classes or styles to a cell. Receives row and column objects.</td></tr>
      <tr><td>local</td><td>Boolean</td><td>false</td><td>Enable client-side processing. If false, server mode is used.</td></tr>
      <tr><td>showSearch</td><td>Boolean</td><td>false</td><td>Displays a search input for filtering rows.</td></tr>
      <tr><td>loading</td><td>Boolean</td><td>false</td><td>Shows a loading overlay.</td></tr>
      <tr><td>columnChooser</td><td>Boolean</td><td>true</td><td>Enable column visibility settings and page size customization.</td></tr>
      <tr><td>section</td><td>String</td><td>null</td><td>Unique key to persist column settings in local storage. Required when using columnChooser.</td></tr>
      <tr><td>mobileDevices</td><td>Boolean</td><td>false</td><td>Enable mobile layout (rows displayed as cards).</td></tr>
      <tr><td>stickyHeader</td><td>Boolean</td><td>false</td><td>Fix table header at top during scroll.</td></tr>
      <tr><td>rowClick</td><td>Boolean</td><td>false</td><td>Enable row click events.</td></tr>
      <tr><td>showCreate</td><td>Boolean</td><td>false</td><td>Show "Create" button above the table.</td></tr>
      <tr>
        <td>storeSearch</td>
        <td>Boolean</td>
        <td>false</td>
        <td>
          Enables automatic saving and restoring of the search input using <code>localStorage</code>.
        </td>
      </tr>
      <tr>
        <td>enablePagination</td>
        <td>Boolean | null</td>
        <td>null</td>
        <td>
          Controls visibility of the pagination component.<br /><br />

          <strong>Behavior:</strong>
          <ul>
            <li><code>true</code> – Always show pagination.</li>
            <li><code>false</code> – Always hide pagination.</li>
            <li><code>null</code> – Auto mode:
              <ul>
                <li>Pagination is shown when <code>local = true</code> (client-side mode)</li>
                <li>Pagination is shown when <code>params</code> is used (server-side mode)</li>
                <li>Otherwise, pagination is hidden</li>
              </ul>
            </li>
          </ul>

          <em>Note: This prop controls only the visibility of the pagination UI, not the data processing logic.</em>
        </td>
      </tr>
      </tbody>
    </table>

    <h4>🎨 Custom Row & Cell Classes</h4>
    <p>You can dynamically style rows and cells using <code>:row-class</code> and <code>:cell-class</code> props:</p>

    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField
        :data="users"
        :columns="columns"
        :row-class="row =&gt; row.status == '1' ? '#ffdede' : ''"
        :cell-class="(row, col) =&gt; col.key === 'name' &amp;&amp; row.name === '__04_test' ? '#ffeecc' : ''"
      /&gt;
    </code></pre>

    <p><strong>Explanation:</strong></p>
    <ul>
      <li><code>:row-class</code> – applies a background color to the entire row if the <code>status</code> is <code>1</code>.</li>
      <li><code>:cell-class</code> – applies a background color to a specific cell (column "name") if the <code>name</code> equals <code>__04_test</code>.</li>
      <li>Both functions return a CSS color or class name, and receive the row object and column definition as arguments.</li>
    </ul>

    <h3>Slots Examples</h3>
    <h4>1️⃣ Custom Header Filter</h4>
    <p>Add a custom filter in the table header using <code>th-&lt;columnKey&gt;</code> slot:</p>
    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField :data="users" :columns="columns" :params="params"&gt;
        &lt;template #th-status="{ setFilter }"&gt;
          &lt;select @change="e =&gt; setFilter(e.target.value)"&gt;
            &lt;option value=""&gt;All&lt;/option&gt;
            &lt;option value="1"&gt;Active&lt;/option&gt;
            &lt;option value="0"&gt;Disabled&lt;/option&gt;
          &lt;/select&gt;
        &lt;/template&gt;
      &lt;/TableField&gt;
    </code></pre>

    <h4>2️⃣ Custom Cell Rendering</h4>
    <p>Override how individual cells render using <code>cell-&lt;columnKey&gt;</code> slot:</p>
    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField :data="users" :columns="columns"&gt;
        &lt;template #cell-status="{ value }"&gt;
          &lt;span :class="value == 1 ? 'badge bg-success' : 'badge bg-danger'"&gt;
            <span v-pre>{{ value == 1 ? 'Active' : 'Disabled' }}</span>
          &lt;/span&gt;
        &lt;/template&gt;
      &lt;/TableField&gt;
    </code></pre>

    <h4>3️⃣ Custom Filters Section</h4>
    <p>Inject a filter bar above the table using the <code>filters</code> slot:</p>
    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField :data="users" :columns="columns" :params="params"&gt;
        &lt;template #filters&gt;
          &lt;select v-model="statusFilter"&gt;
            &lt;option value=""&gt;All&lt;/option&gt;
            &lt;option value="1"&gt;Active&lt;/option&gt;
            &lt;option value="0"&gt;Disabled&lt;/option&gt;
          &lt;/select&gt;

          &lt;select v-model="typeFilter" class="ms-2"&gt;
            &lt;option value=""&gt;All Types&lt;/option&gt;
            &lt;option value="1"&gt;Type 1&lt;/option&gt;
            &lt;option value="2"&gt;Type 2&lt;/option&gt;
          &lt;/select&gt;
        &lt;/template&gt;
      &lt;/TableField&gt;
    </code></pre>

    <hr />

    <h3>Events</h3>
    <table class="table table-striped">
      <thead>
      <tr><th>Event</th><th>Description</th></tr>
      </thead>
      <tbody>
      <tr><td>fetch-data</td><td>Triggered when table needs data (server mode)</td></tr>
      <tr><td>update:params</td><td>Updates the table state</td></tr>
      <tr><td>row-click</td><td>Triggered when a row is clicked</td></tr>
      <tr><td>onCreate</td><td>Triggered when create action is clicked</td></tr>
      <tr><td>action</td><td>Triggered on row-specific actions</td></tr>
      </tbody>
    </table>

    <hr />

    <h3>💾 Search Persistence (storeSearch)</h3>

    <h4>📖 Description</h4>
    <p>
      When <code>storeSearch</code> is enabled, the component will:
    </p>
    <ul>
      <li>💾 Automatically save the search value to <code>localStorage</code> on change</li>
      <li>♻️ Restore the saved search value when the component is mounted</li>
      <li>🔄 Emit updated <code>params</code> with the restored search value</li>
    </ul>

    <p>
      <strong>Note:</strong> The component does <strong>not</strong> trigger data fetching automatically.
      You must handle API calls in the parent component (e.g., via <code>watch(params)</code>).
    </p>

    <h4>🗂 Storage Key</h4>
    <p>
      The search value is stored using a unique key based on the <code>section</code> prop:
    </p>

    <pre class="p-2 bg-white text-dark"><code>
      ${section}_params_search
    </code></pre>

    <p>
      <strong>Example:</strong>
    </p>

    <pre class="p-2 bg-white text-dark"><code>
      section = "users"

      // localStorage key:
      users_params_search
    </code></pre>

    <h4>🚀 Usage Example</h4>

    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField
        v-model:params="params"
        :data="users"
        :columns="columns"
        showSearch
        storeSearch
        section="users"
        @fetch-data="loadUsers"
      /&gt;
    </code></pre>

    <h4>⚠️ Important Notes</h4>
    <ul>
      <li><code>section</code> must be provided for persistence to work</li>
      <li>Clearing the search input removes it from <code>localStorage</code></li>
      <li>Each table should use a unique <code>section</code> value</li>
    </ul>


    <hr />

    <h3>⚡ Row Actions</h3>

    <p>
      You can define action buttons inside a special column with <code>key: "actions"</code>.
      These buttons trigger the <code>action</code> event, allowing you to handle row-specific operations.
    </p>

    <h4>📦 Column Configuration</h4>

    <pre class="p-2 bg-white text-dark"><code class="language-javascript">
      {
        key: "actions",
        title: "Actions",
        actions: [
          {
            emit: "showComment",
            icon: "bi bi-plus-circle",
            class: "btn-sm btn-primary",
            tooltip: "Show comment"
          },
          {
            emit: "showObjectInfo",
            icon: "bi bi-info-circle",
            class: "btn-sm btn-info",
            tooltip: "Show object info"
          }
        ]
      }
    </code></pre>

    ---

    <h4>⚙️ Action Object Structure</h4>

    <table class="table table-striped">
      <thead>
      <tr>
        <th>Property</th>
        <th>Type</th>
        <th>Description</th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <td>emit</td>
        <td>String</td>
        <td>Unique action identifier emitted to parent component</td>
      </tr>
      <tr>
        <td>icon</td>
        <td>String</td>
        <td>Icon class (e.g. Bootstrap Icons)</td>
      </tr>
      <tr>
        <td>class</td>
        <td>String</td>
        <td>CSS classes for button styling</td>
      </tr>
      <tr>
        <td>tooltip</td>
        <td>String</td>
        <td>Tooltip text shown on hover</td>
      </tr>
      </tbody>
    </table>

    ---

    <h4>📡 Event Handling</h4>

    <p>
      When an action button is clicked, the component emits an <code>action</code> event:
    </p>

    <pre class="p-2 bg-white text-dark"><code class="language-javascript">
      this.$emit('action', {
        type: act.emit,
        row: row
      });
    </code></pre>

    ---

    <h4>🧩 Usage Example</h4>

    <pre class="p-2 bg-white text-dark"><code class="language-html">
      &lt;TableField
        :data="users"
        :columns="columns"
        @action="handleAction"
      /&gt;
    </code></pre>

    <pre class="p-2 bg-white text-dark"><code class="language-javascript">
      methods: {
        handleAction({ type, row }) {
          switch (type) {
            case 'showComment':
              this.openCommentModal(row);
              break;

            case 'showObjectInfo':
              this.openInfoModal(row);
              break;
          }
        }
      }
    </code></pre>

    ---

    <h4>⚠️ Notes</h4>

    <ul>
      <li>Action buttons are rendered automatically when <code>key: "actions"</code> is present</li>
      <li>Each action must have a unique <code>emit</code> value</li>
      <li>Clicking an action button does <strong>not</strong> trigger <code>row-click</code></li>
      <li>You can define multiple actions per row</li>
    </ul>
  </div>

  <h2>🧩 Example Table</h2>
  <div class="container-fluid">
    <div class="card border-primary shadow-lg">
      <div class="card-header text-white bg-primary mb-3">
        <label>title</label>
      </div>

      <div class="card-body">
        <TableField
            v-model:params="params"
            :data="objects"
            :columns="headers"
            :showSearch="true"
            :local="false"
            :loading="loading"
            section="demo-table-field"
            :mobileDevices="true"
            :row-class="row => row.vehicleNumber == 'a21' ? '#ffdede' : ''"
            :cell-class="(row, col) => col.key === 'name' && row.name === '__04_test' ? '#ffeecc' : ''"
            :sticky-header="true"
            :rowClick="true"
            :showCreate="true"
            @onCreate="onCreate"
            @row-click="onRowClick"
            @fetch-data="fetchData"
            @action="handleAction"
        >
          <template #filters>
            <AdvancedSelect
                :model-value="statusFilter"
                :items="[
                  { text: 'All', value: '' },
                  { text: 'Active', value: '1' },
                  { text: 'Disabled', value: '0' }
                ]"
                @update:modelValue="val => statusFilter = val"
                class="col-12 col-lg-4 col-xl-3 col-xxl-2 col-sm-5 my-1"
            />

            <AdvancedSelect
                :model-value="typeFilter"
                :items="typeOptions"
                @update:modelValue="val => typeFilter = val"
                class="col-12 col-lg-4 col-xl-3 col-xxl-2 col-sm-5 ms-2 my-1"
            />
          </template>

          <template #th-status="{ setFilter }">
            <AdvancedSelect
                :model-value="params.filter?.status || null"
                :items="[
                  { text: 'All', value: '' },
                  { text: 'Active', value: '1' },
                  { text: 'Disabled', value: '0' },
                ]"
                @update:modelValue="val => setFilter(val)"
            />
          </template>

          <template #th-vehicleNumber="{ setFilter }">
            <AdvancedSelect
                :model-value="params.filter?.vehicleNumber || null"
                :items="[
                  { text: 'All', value: '' },
                  { text: 'a21', value: '1' },
                  { text: '0001', value: '0' }
                ]"
                @update:modelValue="val => setFilter(val)"
            />
          </template>

          <template #cell-status="{ value }">
            <span :class="`badge rounded-pill ${ value == 1 ? 'bg-success' : 'bg-danger' }`">
              {{ value == 1 ? 'active' : 'disabled' }}
            </span>
          </template>
        </TableField>
      </div>
    </div>
  </div>
</template>

<script>
import TableField from "@/components/common/table-field/TableField.vue";
import AdvancedSelect from "@/components/common/advanced-select/AdvancedSelect.vue";
export default {
  name: 'TableFieldDemo',

  components: {
    AdvancedSelect,
    TableField
  },

  data() {
    return {
      params: {
        page: 1,
        pageSize: 20,
        total: null,
        last_page: null,
        search: null,
        sort_column: null,
        sort_direction: null,
      },
      loading: false,
      objects: [],
      isMobileActive: this.mobileDevices && window.innerWidth < 768,
      statusFilter: '',
      typeFilter: '',
      typeOptions: [
        { text: 'All', value: '' },
        { text: 'Type 1', value: '1' },
        { text: 'Type 2', value: '2' },
      ],
    }
  },

  computed: {
    headers() {
      return [
        { title: this.$t('object.monitoring.number'), key: "vehicleNumber", sortable: true, rowClick: true },
        { title: this.$t('object.monitoring.name'), key: "name", sortable: true, rowClick: true },
        { title: this.$t('object.monitoring.status'), key: "status", sortable: true, rowClick: true },
        { title: this.$t('object.monitoring.comment'), key: "comment", sortable: true, rowClick: true },
        {
          key: "actions",
          rowClick: false,
          actions: [
            { class: 'btn-success btn-sm', emit: 'onEdit', icon: 'bi bi-pencil-square', tooltip: this.$t('table-field.action.edit') },
            { class: 'btn-danger btn-sm', emit: 'onDelete', icon: 'bi bi-trash3', tooltip: this.$t('table-field.action.delete') }
          ]
        },
      ]
    }
  },

  methods: {
    async fetchData() {
      console.log(this.params);
      this.populateData();
    },

    async populateData() {
      this.loading = true;
      const savedPageSize = localStorage.getItem(`demo-table-field-page-size`);
      this.params.pageSize = savedPageSize != null ? Number(savedPageSize) : (this.params.pageSize != null ? Number(this.params.pageSize) : 20);
      try {

        const response = await this.ApiRequest.requestAsync(
            `object/monitoring`,
            this.ApiRequest.REQUEST_POST,
            this.params,
            'my-vehicles-search',
            true
        );

        if (!response || response.error || response.canceled) return;
        const { data, pagination } = response;
        this.objects = this.BasicHelper.cloneObject(data.objects);
        Object.assign(this.params, pagination);
      } finally {
        this.loading = false;
      }
    },

    handleAction({ type, row }) {
      if (type === 'onEdit') {
        this.onEdit(row);
      }
      if (type === 'onDelete') {
        this.onDelete(row);
      }
    },

    onEdit(row) {
      console.log("Edit row:", row);
    },

    onDelete(row) {
      console.log("Delete row:", row);
    },

    onCreate() {
      alert('create:');
    },

    onRowClick(row) {
      alert('Row clicked:');
    },

    // restoreWidth() {
    //   Object.defineProperty(window, 'innerWidth', { writable: true, configurable: true, value: this.originalWidth });
    //   window.dispatchEvent(new Event('resize'));
    // }
  },

  mounted() {
    // Object.defineProperty(window, 'innerWidth', { writable: true, configurable: true, value: 500 });
    // window.dispatchEvent(new Event('resize'));
  },

  beforeMount() {
    this.populateData();
  },
}
</script>
